import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader2, ChevronLeft, FileText, Calendar, CheckCircle, AlertCircle, MessageSquare } from 'lucide-react';
import { getMeetingMinutes, getMeetingMinute, submitMinuteComment } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import PDFViewer from '../../components/PDFViewer';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const MeetingMinutesPage = () => {
  const { user } = useAuth();
  const [minutes, setMinutes] = useState([]);
  const [selectedMinute, setSelectedMinute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  // Comment form state
  const [comment, setComment] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentSuccess, setCommentSuccess] = useState(false);
  const [commentError, setCommentError] = useState(null);

  useEffect(() => {
    const fetchMinutes = async () => {
      try {
        const response = await getMeetingMinutes();
        setMinutes(response.data.minutes || []);
      } catch (error) {
        console.error('Failed to fetch minutes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMinutes();
  }, []);

  const handleViewMinute = async (id) => {
    setDetailLoading(true);
    setCommentSuccess(false);
    setCommentError(null);
    setComment('');
    try {
      const response = await getMeetingMinute(id);
      setSelectedMinute(response.data);
    } catch (error) {
      console.error('Failed to fetch minute:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedMinute(null);
    setCommentSuccess(false);
    setCommentError(null);
    setComment('');
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    setCommentSubmitting(true);
    setCommentError(null);

    try {
      await submitMinuteComment(selectedMinute.id, comment);
      setCommentSuccess(true);
      setComment('');
    } catch (error) {
      setCommentError('Failed to submit your comment. Please try again.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-12 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // Detail View
  if (selectedMinute) {
    return (
      <div className="p-6 md:p-12" data-testid="minute-detail">
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-4 -ml-2"
          data-testid="back-to-minutes-btn"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Minutes
        </Button>

        <div className="max-w-4xl">
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-testid="minute-title">
                  {selectedMinute.title}
                </h1>
                <p className="text-gray-500 flex items-center gap-2" data-testid="minute-date">
                  <Calendar className="h-4 w-4" />
                  {formatDate(selectedMinute.date)}
                </p>
              </div>

              {/* Content */}
              {(() => {
                // Debug logging
                console.log('Selected Minute Data:', selectedMinute);
                console.log('Type field:', selectedMinute.type);
                console.log('ContentType field:', selectedMinute.contentType);
                console.log('URL field:', selectedMinute.url);

                // Check if it's a PDF (case-insensitive)
                const isPDF =
                  selectedMinute.type?.toLowerCase() === 'pdf' ||
                  selectedMinute.contentType?.toLowerCase() === 'pdf' ||
                  selectedMinute.url?.toLowerCase().endsWith('.pdf');

                console.log('Is PDF?', isPDF);

                return isPDF ? (
                  <div className="mb-8">
                    <PDFViewer url={selectedMinute.url} title={selectedMinute.title} />
                  </div>
                ) : (
                  <Card className="border-gray-100 mb-8">
                    <CardContent className="p-6">
                      <div
                        className="prose prose-gray max-w-none"
                        dangerouslySetInnerHTML={{ __html: selectedMinute.content }}
                        data-testid="minute-text-content"
                      />
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Comment Form */}
              <Card className="border-gray-100 bg-gradient-to-br from-gray-50 to-white" data-testid="comment-form-card">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-emerald-600" />
                    Comment About This Minute
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {commentSuccess ? (
                    <div className="text-center py-4" data-testid="comment-success">
                      <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                        <CheckCircle className="h-5 w-5 text-emerald-600" />
                      </div>
                      <p className="text-gray-700 mb-3">Your comment has been submitted successfully.</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCommentSuccess(false)}
                      >
                        Submit Another Comment
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmitComment} className="space-y-4">
                      {commentError && (
                        <Alert variant="destructive" data-testid="comment-error">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>{commentError}</AlertDescription>
                        </Alert>
                      )}

                      {/* Pre-populated fields (locked) */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="member-name">Your Name</Label>
                          <Input
                            id="member-name"
                            value={`${user?.firstName} ${user?.lastName}`}
                            disabled
                            className="bg-gray-100"
                            data-testid="comment-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="member-email">Your Email</Label>
                          <Input
                            id="member-email"
                            value={user?.email}
                            disabled
                            className="bg-gray-100"
                            data-testid="comment-email-input"
                          />
                        </div>
                      </div>

                      {/* Hidden fields for reference */}
                      <div className="text-sm text-gray-500 p-3 bg-gray-100 rounded-md">
                        <p><strong>Regarding:</strong> {selectedMinute.title}</p>
                        <p><strong>Date:</strong> {formatDate(selectedMinute.date)}</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="comment">Your Comment or Query</Label>
                        <Textarea
                          id="comment"
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          placeholder="Enter your comment or query about this meeting minute..."
                          rows={4}
                          required
                          data-testid="comment-textarea"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="bg-emerald-600 hover:bg-emerald-700"
                        disabled={commentSubmitting}
                        data-testid="comment-submit-btn"
                      >
                        {commentSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Submit Comment
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="p-6 md:p-12" data-testid="minutes-page">
      <div className="max-w-4xl">
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-2">
          Meeting Minutes
        </h1>
        <p className="text-gray-600 mb-8">
          Access minutes from lodge meetings. Members can submit comments or queries about specific meetings.
        </p>

        {/* Minutes List */}
        <div className="space-y-4" data-testid="minutes-list">
          {minutes.map((minute) => (
            <Card
              key={minute.id}
              className="border-gray-100 hover:shadow-md transition-all cursor-pointer"
              onClick={() => handleViewMinute(minute.id)}
              data-testid={`minute-card-${minute.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{minute.title}</h3>
                      <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(minute.date)}
                      </p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${minute.type === 'pdf' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                    {minute.type === 'pdf' ? 'PDF' : 'Text'}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MeetingMinutesPage;
